import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getUserType, canEdit } from "../permissions";
import API_BASE from "../config";

const EditCheque = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const cheque = location.state?.cheque;
    
    const userType = getUserType();
    const allowEdit = canEdit(userType, "cheques");

    const [form, setForm] = useState({
        id: "",
        payment_id: "",
        booking_id: "",
        bank: "",
        cheque_no: "",
        cheque_date: "",
        amount: "",
        c_status: "",
        clear_date: ""
    });
    
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        if (!cheque) {
            navigate("/dashboard/cheques");
            return;
        }
        if (!allowEdit) {
            navigate("/dashboard/cheques");
            return;
        }
        const populated = {};
        Object.keys(form).forEach(k => {
            populated[k] = cheque[k] !== undefined && cheque[k] !== null ? String(cheque[k]) : "";
        });
        setForm(populated);
    }, [cheque, navigate, allowEdit]);

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const handleSubmit = (e) => {
        e.preventDefault();
        setSaving(true);
        setError("");
        setSuccess("");

        const formData = new URLSearchParams();
        Object.keys(form).forEach((key) => {
            if (key !== "id") formData.append(key, form[key]);
        });
        
        // Ensure amount is not empty to avoid API issues for numeric fields
        if(!form.amount) formData.set("amount", "0");

        fetch(`${API_BASE}/api/updatedatas`, {
            method: "POST",
            headers: { 
                Authorization: "1", 
                Table: "cheques",
                Id: cheque.id
            },
            body: formData
        })
            .then(res => res.json())
            .then(json => {
                setSaving(false);
                if (json.status === "Success" || json.status === true) {
                    setSuccess("Cheque details updated successfully! Returning...");
                    setTimeout(() => navigate(-1), 1500);
                } else {
                    setError(json.message || "Failed to update cheque details.");
                }
            })
            .catch(() => {
                setSaving(false);
                setError("Network error updating cheque details.");
            });
    };

    if (!cheque) return null;

    return (
        <div className="ep-page">
            <style>{`
        .ep-page { max-width:900px; margin:0 auto; padding-bottom:40px; }
        .ep-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:24px; }
        .ep-back { background:none; border:none; color:#64748b; cursor:pointer; font-size:14px; font-weight:600; font-family:'Inter',sans-serif; display:flex; align-items:center; gap:6px; transition:color .2s; }
        .ep-back:hover { color:#0f172a; }
        .ep-title { font-size:24px; font-weight:700; color:#0f172a; }
        
        .ep-card { background:#fff; border-radius:16px; border:1px solid #e2e8f0; box-shadow:0 4px 12px rgba(0,0,0,.03); border-top:4px solid #3b82f6; overflow:hidden; }
        .ep-head { padding:20px 24px; border-bottom:1px solid #f1f5f9; background:#f8fafc; }
        .ep-h-title { font-size:16px; font-weight:600; color:#0f172a; }
        .ep-h-sub { font-size:13px; color:#64748b; margin-top:2px; }
        
        .ep-form { padding:24px; }
        .ep-sec { font-size:14px; font-weight:700; color:#475569; text-transform:uppercase; letter-spacing:1px; margin:0 0 16px; display:flex; align-items:center; gap:8px; }
        
        .ep-grid { display:grid; grid-template-columns:1fr 1fr; gap:20px; margin-bottom:32px; }
        @media(max-width:640px) { .ep-grid { grid-template-columns:1fr; } }
        
        .ep-field { display:flex; flex-direction:column; gap:6px; }
        .ep-field.full { grid-column:1/-1; }
        .ep-field label { font-size:13px; font-weight:600; color:#334155; }
        .ep-field input, .ep-field select, .ep-field textarea { padding:10px 14px; border-radius:10px; border:1px solid #cbd5e1; font-size:14px; font-family:'Inter',sans-serif; color:#0f172a; outline:none; transition:all .2s; background:#fff; }
        .ep-field input:focus, .ep-field select:focus, .ep-field textarea:focus { border-color:#3b82f6; box-shadow:0 0 0 3px rgba(59,130,246,.1); }
        .ep-field input:disabled { background:#f1f5f9; color:#64748b; cursor:not-allowed; }
        
        .ep-foot { padding:20px 24px; border-top:1px solid #f1f5f9; background:#f8fafc; display:flex; justify-content:flex-end; gap:12px; }
        .ep-btn-canc { padding:10px 20px; border-radius:10px; border:1px solid #cbd5e1; background:#fff; color:#334155; font-weight:600; font-size:14px; cursor:pointer; transition:all .2s; }
        .ep-btn-canc:hover { background:#f1f5f9; }
        .ep-btn-save { padding:10px 20px; border-radius:10px; border:none; background:#2563eb; color:#fff; font-weight:600; font-size:14px; cursor:pointer; transition:all .2s; display:flex; align-items:center; gap:8px; }
        .ep-btn-save:hover { background:#1d4ed8; }
        .ep-btn-save:disabled { opacity:.7; cursor:not-allowed; }
        
        .ep-msg { padding:12px 16px; border-radius:8px; font-size:14px; font-weight:500; margin-bottom:20px; }
        .ep-err { background:#fef2f2; color:#dc2626; border:1px solid #fecaca; }
        .ep-succ { background:#f0fdf4; color:#16a34a; border:1px solid #bbf7d0; }
      `}</style>
            
            <div className="ep-header">
                <button className="ep-back" onClick={() => navigate(-1)}>← Back</button>
                <h1 className="ep-title">Edit Cheque</h1>
                <div style={{ width: 60 }} />
            </div>

            {error && <div className="ep-msg ep-err">⚠️ {error}</div>}
            {success && <div className="ep-msg ep-succ">✅ {success}</div>}

            <div className="ep-card">
                <div className="ep-head">
                    <div className="ep-h-title">Cheque ID: {cheque.id}</div>
                    <div className="ep-h-sub">Payment: {cheque.payment_id} | Booking: {cheque.booking_id}</div>
                </div>

                <form className="ep-form" onSubmit={handleSubmit}>
                    <div className="ep-sec">🏦 Cheque Information</div>
                    <div className="ep-grid">
                        <div className="ep-field">
                            <label>Payment ID</label>
                            <input name="payment_id" value={form.payment_id} disabled />
                        </div>
                        <div className="ep-field">
                            <label>Booking ID</label>
                            <input name="booking_id" value={form.booking_id} disabled />
                        </div>
                        <div className="ep-field">
                            <label>Bank</label>
                            <input name="bank" value={form.bank} onChange={handleChange} placeholder="e.g. SBI, HDFC" />
                        </div>
                        <div className="ep-field">
                            <label>Cheque No</label>
                            <input name="cheque_no" value={form.cheque_no} onChange={handleChange} />
                        </div>
                        <div className="ep-field">
                            <label>Cheque Date</label>
                            <input name="cheque_date" value={form.cheque_date} onChange={handleChange} placeholder="DD/MM/YY" />
                        </div>
                        <div className="ep-field">
                            <label>Amount (₹)</label>
                            <input name="amount" type="number" value={form.amount} onChange={handleChange} />
                        </div>
                    </div>

                    <div className="ep-sec">🛠 Status</div>
                    <div className="ep-grid">
                        <div className="ep-field">
                            <label>Cheque Status</label>
                            <select name="c_status" value={form.c_status} onChange={handleChange}>
                                <option value="">Select Status</option>
                                <option value="Received">Received</option>
                                <option value="Cleared">Cleared</option>
                                <option value="Pending">Pending</option>
                                <option value="Bounced">Bounced</option>
                            </select>
                        </div>
                        <div className="ep-field">
                            <label>Clear Date</label>
                            <input name="clear_date" value={form.clear_date} onChange={handleChange} placeholder="DD/MM/YY" />
                        </div>
                    </div>

                    <div className="ep-foot">
                        <button type="button" className="ep-btn-canc" onClick={() => navigate(-1)}>Cancel</button>
                        <button type="submit" className="ep-btn-save" disabled={saving}>
                            {saving ? "Saving..." : "💾 Save Changes"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditCheque;
